import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import dayjs from 'dayjs';
import {
  createAssignment,
  createPrinter,
  createPrinterCatalog,
  createPrinterEvent,
  createTonerStock,
  deletePrinter,
  deletePrinterCatalog,
  deleteTonerStock,
  getPrinterCatalogs,
  getPrinterDashboard,
  getPrinterEvents,
  getTonerStocks,
  updateAssignment,
  updatePrinter,
  updatePrinterCatalog,
  updateTonerStock
} from '../../services/printerService';
import { message, Modal as AntdModal } from 'antd'; // Add antd message and aliased Modal

const MUNICIPAL_DEPARTMENTS = [
  { value: 'Bilgi Islem Mudurlugu', label: 'Bilgi İşlem Müdürlüğü' },
  { value: 'Genclik Ve Spor Hizmetleri Mudurlugu', label: 'Gençlik ve Spor Hizmetleri Müdürlüğü' },
  { value: 'Zabita Mudurlugu', label: 'Zabıta Müdürlüğü' },
  { value: 'Muhtarliklar Mudurlugu', label: 'Muhtarlıklar Müdürlüğü' },
  { value: 'Veteriner Isleri Mudurlugu', label: 'Veteriner İşleri Müdürlüğü' },
  { value: 'Basin Yayin Ve Halkla Iliskiler Mudurlugu', label: 'Basın Yayın ve Halkla İlişkiler Müdürlüğü' },
  { value: 'Kultur Ve Sosyal Isler Mudurlugu', label: 'Kültür ve Sosyal İşler Müdürlüğü' },
  { value: 'Imar Ve Sehircilik Mudurlugu', label: 'İmar ve Şehircilik Müdürlüğü' },
  { value: 'Hukuk Isleri Mudurlugu', label: 'Hukuk İşleri Müdürlüğü' },
  { value: 'Iklim Degisikligi Ve Sifir Atik Mudurlugu', label: 'İklim Değişikliği ve Sıfır Atık Müdürlüğü' },
  { value: 'Temizlik Isleri Mudurlugu', label: 'Temizlik İşleri Müdürlüğü' },
  { value: 'Insan Kaynaklari Ve Egitim Mudurlugu', label: 'İnsan Kaynakları ve Eğitim Müdürlüğü' },
  { value: 'Isletme Ve Istirakler Mudurlugu', label: 'İşletme ve İştirakler Müdürlüğü' },
  { value: 'Destek Hizmetleri Mudurlugu', label: 'Destek Hizmetleri Müdürlüğü' },
  { value: 'Fen Isleri Mudurlugu', label: 'Fen İşleri Müdürlüğü' },
  { value: 'Makine Ikmal Bak????m Ve Onarim Mudurlugu', label: 'Makine İkmal Bakım ve Onarım Müdürlüğü' },
  { value: 'Gecekondu Ve Sosyal Konut Mudurlugu', label: 'Gecekondu ve Sosyal Konut Müdürlüğü' },
  { value: 'Afet Isleri Mudurlugu', label: 'Afet İşleri Müdürlüğü' },
  { value: 'Park Ve Bahceler Mudurlugu', label: 'Park ve Bahçeler Müdürlüğü' },
  { value: 'Emlak Istimlak Mudurlugu', label: 'Emlak İstimlak Müdürlüğü' },
  { value: 'Yazi Isleri Mudurlugu', label: 'Yazı İşleri Müdürlüğü' },
  { value: 'Teftis Kurulu Mudurlugu', label: 'Teftiş Kurulu Müdürlüğü' },
  { value: 'Ozel Kalem Mudurlugu', label: 'Özel Kalem Müdürlüğü' },
  { value: 'Yapi Kontrol Mudurlugu', label: 'Yapı Kontrol Müdürlüğü' },
  { value: 'Strateji Ve Gelistirme Mudurlugu', label: 'Strateji ve Geliştirme Müdürlüğü' },
  { value: 'Mali Hizmetler Mudurlugu', label: 'Mali Hizmetler Müdürlüğü' }
];

const getDepartmentLabel = (value) => {
  const match = MUNICIPAL_DEPARTMENTS.find((department) => department.value === value);
  return match ? match.label : value;
};

const EVENT_TYPE_LABELS = {
  toner_change: 'Toner Değişimi',
  fault: 'Arıza Kaydı',
  maintenance: 'Bakım Kaydı'
};

const buildInitialPrinterForm = () => ({
  name: '',
  departmentName: '',
  roomName: '',
  roomNumber: '',
  personName: '',
  printerCatalogGuid: '',
  notes: '',
  assignmentGuid: '',
  printerGuid: '',
});

const buildInitialCatalogForm = () => ({
  guid: null,
  brand: '',
  model: '',
  tonerStockGuids: [''],
  notes: ''
});

const buildInitialStockForm = () => ({
  guid: null,
  name: '',
  quantity: 0,
  notes: ''
});

const initialEventForm = (printerGuid = '') => ({
  printerGuid,
  eventType: 'toner_change',
  description: '',
  printerCatalogTonerGuid: '',
  eventDate: dayjs().format('YYYY-MM-DDTHH:mm')
});

const collectToners = (printer, catalogs) => {
  if (!printer) return [];
  const items = [];

  if (Array.isArray(printer.catalogTonerItems) && printer.catalogTonerItems.length) {
    printer.catalogTonerItems.forEach((toner) => {
      items.push({
        guid: toner.guid,
        tonerStockGuid: toner.tonerStockGuid,
        name: toner.tonerStockName
      });
    });
  }

  if (!items.length && printer.printerCatalogGuid) {
    const catalog = catalogs.find((item) => item.guid === printer.printerCatalogGuid);
    catalog?.toners?.forEach((toner) => {
      items.push({
        guid: toner.guid,
        tonerStockGuid: toner.tonerStockGuid,
        name: toner.tonerStockName
      });
    });
  }

  return items;
};

const PrinterManagement = () => {
  const [loading, setLoading] = useState(true);
  const [printers, setPrinters] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [tonerStocks, setTonerStocks] = useState([]);
  const [events, setEvents] = useState([]);

  const [selectedDepartment, setSelectedDepartment] = useState(MUNICIPAL_DEPARTMENTS[0]?.value || '');
  const [selectedPrinterId, setSelectedPrinterId] = useState('');

  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [printerForm, setPrinterForm] = useState(buildInitialPrinterForm());
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [savingPrinter, setSavingPrinter] = useState(false);

  const [catalogForm, setCatalogForm] = useState(buildInitialCatalogForm());
  const [savingCatalog, setSavingCatalog] = useState(false);
  const [activePanel, setActivePanel] = useState('');
  const [stockForm, setStockForm] = useState(buildInitialStockForm());
  const [savingStock, setSavingStock] = useState(false);

  const [showEventModal, setShowEventModal] = useState(false);
  const [eventForm, setEventForm] = useState(initialEventForm());
  const [savingEvent, setSavingEvent] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedPrinterId) {
      loadEvents(selectedPrinterId);
    } else {
      setEvents([]);
    }
  }, [selectedPrinterId]);

  const departmentOptions = useMemo(() => {
    const set = new Set(MUNICIPAL_DEPARTMENTS.map((department) => department.value));
    printers.forEach((printer) => {
      printer.assignments?.forEach((assignment) => {
        if (assignment.departmentName) {
          set.add(assignment.departmentName);
        }
      });
    });
    return Array.from(set).map((value) => ({
      value,
      label: getDepartmentLabel(value)
    }));
  }, [printers]);

  useEffect(() => {
    const values = departmentOptions.map((option) => option.value);
    if (values.length && !values.includes(selectedDepartment)) {
      setSelectedDepartment(values[0]);
    }
  }, [departmentOptions, selectedDepartment]);

  const departmentPrinters = useMemo(() => {
    if (!selectedDepartment) return printers;
    return printers.filter((printer) =>
      printer.assignments?.some((assignment) => assignment.departmentName === selectedDepartment && assignment.isActive)
    );
  }, [printers, selectedDepartment]);

  useEffect(() => {
    if (!departmentPrinters.length) {
      setSelectedPrinterId('');
      return;
    }

    if (!departmentPrinters.some((printer) => printer.guid === selectedPrinterId)) {
      setSelectedPrinterId(departmentPrinters[0].guid);
    }
  }, [departmentPrinters, selectedPrinterId]);

  const selectedPrinter = useMemo(
    () => departmentPrinters.find((printer) => printer.guid === selectedPrinterId) || null,
    [departmentPrinters, selectedPrinterId]
  );

  const modalPrinter = useMemo(() => {
    if (!eventForm.printerGuid) return selectedPrinter;
    return printers.find((printer) => printer.guid === eventForm.printerGuid) || selectedPrinter;
  }, [eventForm.printerGuid, printers, selectedPrinter]);

  const availableModalToners = useMemo(
    () => collectToners(modalPrinter, catalogs),
    [modalPrinter, catalogs]
  );

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadDashboard(), loadCatalogs(), loadTonerStocks()]);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await getPrinterDashboard();
      if (response?.isSuccess) {
        setPrinters(response.data || []);
      }
    } catch (error) {
      console.error('Toner Yönetimi yüklenemedi:', error);
      message.error('Toner Yönetimi verileri yüklenemedi.');
    }
  };

  const loadCatalogs = async () => {
    try {
      const response = await getPrinterCatalogs();
      if (response?.isSuccess) {
        setCatalogs(response.data || []);
      }
    } catch (error) {
      console.error('Kullanılan Yazıcılar listesi alınamadı:', error);
      message.error('Kullanılan Yazıcılar listesi yüklenemedi.');
    }
  };

  const loadTonerStocks = async () => {
    try {
      const response = await getTonerStocks();
      if (response?.isSuccess) {
        setTonerStocks(response.data || []);
      }
    } catch (error) {
      console.error('Toner stokları yüklenemedi:', error);
      message.error('Toner stokları yüklenemedi.');
    }
  };

  const loadEvents = async (printerGuid) => {
    try {
      const response = await getPrinterEvents(printerGuid, 200);
      if (response?.isSuccess) {
        setEvents(response.data || []);
      }
    } catch (error) {
      console.error('Kayıtlar okunamadı:', error);
      message.error('Kayıtlar okunamadı.');
    }
  };

  const handleOpenPrinterModal = () => {
    setEditingPrinter(null);
    setPrinterForm({
      ...buildInitialPrinterForm(),
      departmentName: selectedDepartment,
      printerCatalogGuid: catalogs[0]?.guid || ''
    });
    setShowPrinterModal(true);
  };

  const handleEditPrinter = (printer, assignment) => {
    setEditingPrinter(printer);
    setPrinterForm({
      name: printer.name,
      departmentName: assignment?.departmentName || selectedDepartment,
      roomName: assignment?.roomName || '',
      roomNumber: assignment?.roomNumber || '',
      personName: assignment?.personName || '',
      printerCatalogGuid: printer.printerCatalogGuid || '',
      notes: assignment?.notes || printer.notes || '',
      assignmentGuid: assignment?.guid || '',
      printerGuid: printer.guid,
    });
    setShowPrinterModal(true);
  };

  const handlePrinterInput = (event) => {
    const { name, value } = event.target;
    setPrinterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePrinter = async (event) => {
    event.preventDefault();
    if (!printerForm.departmentName) {
      message.warning('Birim seçmelisiniz.');
      return;
    }

    if (!printerForm.name.trim()) {
      message.warning('Yazıcı adı zorunludur.');
      return;
    }

    setSavingPrinter(true);
    try {
      const payload = {
        printerCatalogGuid: printerForm.printerCatalogGuid || null,
        name: printerForm.name.trim(),
        departmentName: printerForm.departmentName.trim(),
        roomName: printerForm.roomName?.trim(),
        roomNumber: printerForm.roomNumber?.trim(),
        personName: printerForm.personName?.trim(),
        notes: printerForm.notes?.trim(),
      };

      let response;

      if (editingPrinter) {
        response = await updatePrinter({
          guid: editingPrinter.guid,
          name: payload.name,
          printerCatalogGuid: payload.printerCatalogGuid,
          notes: payload.notes,
          isActive: true,
        });

        if (response?.isSuccess && printerForm.assignmentGuid) {
          await updateAssignment({
            guid: printerForm.assignmentGuid,
            departmentName: payload.departmentName,
            personName: payload.personName,
            roomName: payload.roomName,
            roomNumber: payload.roomNumber,
            notes: payload.notes,
            isActive: true,
          });
        }

        if (payload.departmentName && !printerForm.assignmentGuid) {
          await createAssignment({
            printerGuid: editingPrinter.guid,
            departmentName: payload.departmentName,
            personName: payload.personName,
            roomName: payload.roomName,
            roomNumber: payload.roomNumber,
            notes: payload.notes,
          });
        }
      } else {
        response = await createPrinter({
          ...payload,
        });
      }

      if (response?.isSuccess) {
        if (payload.departmentName) {
          setSelectedDepartment(payload.departmentName);
        }
        if (editingPrinter) {
          setSelectedPrinterId(editingPrinter.guid);
        }
        setShowPrinterModal(false);
        setEditingPrinter(null);
        setPrinterForm(buildInitialPrinterForm());
        await loadDashboard();
        message.success(editingPrinter ? 'Yazıcı güncellendi.' : 'Yazıcı kaydedildi.');
      } else {
        message.error(response?.resultMessage || 'Yazıcı kaydedilemedi.');
      }
    } catch (error) {
      console.error('Yazıcı kaydedilemedi:', error);
      message.error('Yazıcı kaydedilemedi.');
    } finally {
      setSavingPrinter(false);
    }
  };

  const handleCatalogInput = (event) => {
    const { name, value } = event.target;
    setCatalogForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCatalogTonerSelect = (index, value) => {
    setCatalogForm((prev) => {
      const next = [...prev.tonerStockGuids];

      if (!value) {
        next[index] = '';
        return { ...prev, tonerStockGuids: next };
      }

      const alreadyUsed = prev.tonerStockGuids.some((guid, idx) => guid === value && idx !== index);
      if (alreadyUsed) {
        message.warning('Bu toner zaten bu yazıcıya eklenmiş.');
        return prev;
      }

      next[index] = value;
      return { ...prev, tonerStockGuids: next };
    });
  };

  const addCatalogTonerRow = () => {
    setCatalogForm((prev) => ({ ...prev, tonerStockGuids: [...prev.tonerStockGuids, ''] }));
  };

  const removeCatalogTonerRow = (index) => {
    setCatalogForm((prev) => {
      if (prev.tonerStockGuids.length === 1) return prev;
      const next = prev.tonerStockGuids.filter((_, idx) => idx !== index);
      return { ...prev, tonerStockGuids: next.length ? next : [''] };
    });
  };

  const handleCatalogSubmit = async (event) => {
    event.preventDefault();
    if (!catalogForm.brand.trim() || !catalogForm.model.trim()) {
      message.warning('Marka ve model zorunludur.');
      return;
    }

    setSavingCatalog(true);
    try {
      const normalizedToners = [];
      const seen = new Set();
      catalogForm.tonerStockGuids.forEach((tonerGuid) => {
        if (!tonerGuid) {
          return;
        }
        if (seen.has(tonerGuid)) {
          return;
        }
        seen.add(tonerGuid);
        normalizedToners.push(tonerGuid);
      });

      const payload = {
        guid: catalogForm.guid || undefined,
        brand: catalogForm.brand.trim(),
        model: catalogForm.model.trim(),
        tonerStockGuids: normalizedToners,
        notes: catalogForm.notes?.trim()
      };

      const response = catalogForm.guid
        ? await updatePrinterCatalog(payload)
        : await createPrinterCatalog(payload);

      if (response?.isSuccess) {
        setCatalogForm(buildInitialCatalogForm());
        await loadCatalogs();
        message.success('Kayıt başarıyla kaydedildi.');
      } else {
        message.error(response?.resultMessage || 'Kayıt kaydedilemedi.');
      }
    } catch (error) {
      console.error('Kayıt kaydedilemedi:', error);
      message.error('Kayıt kaydedilemedi.');
    } finally {
      setSavingCatalog(false);
    }
  };

  const handleEditCatalog = (catalog) => {
    setCatalogForm({
      guid: catalog.guid,
      brand: catalog.brand,
      model: catalog.model,
      tonerStockGuids:
        catalog.toners?.length ? catalog.toners.map((toner) => toner.tonerStockGuid) : [''],
      notes: catalog.notes || ''
    });
    setActivePanel('catalog');
  };

  const handleDeleteCatalog = async (guid) => {
    AntdModal.confirm({
      title: 'Kayıt Silme',
      content: 'Marka/model kaydını silmek istediğinize emin misiniz?',
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await deletePrinterCatalog(guid);
          if (response?.isSuccess) {
            await loadCatalogs();
            message.success('Kayıt silindi.');
          } else {
            message.error(response?.resultMessage || 'Kayıt silinemedi.');
          }
        } catch (error) {
          console.error('Kayıt silinemedi:', error);
          message.error('Kayıt silinemedi.');
        }
      }
    });
  };

  const handleStockInput = (event) => {
    const { name, value } = event.target;
    setStockForm((prev) => ({
      ...prev,
      [name]: name === 'quantity' ? Number(value) : value
    }));
  };

  const handleOpenStockEditor = (stock) => {
    if (stock) {
      setStockForm({
        guid: stock.guid,
        name: stock.name,
        quantity: stock.quantity,
        notes: stock.notes || ''
      });
    } else {
      setStockForm(buildInitialStockForm());
    }
    setActivePanel('stock');
  };

  const handleSaveStock = async (event) => {
    event.preventDefault();
    if (!stockForm.name.trim()) {
      message.warning('Toner adı zorunludur.');
      return;
    }

    setSavingStock(true);
    try {
      const payload = {
        guid: stockForm.guid || undefined,
        name: stockForm.name.trim(),
        quantity: Number.isNaN(stockForm.quantity) ? 0 : stockForm.quantity,
        notes: stockForm.notes?.trim()
      };

      const response = stockForm.guid
        ? await updateTonerStock(payload)
        : await createTonerStock(payload);

      if (response?.isSuccess) {
        await loadTonerStocks();
        await loadCatalogs();
        await loadDashboard();
        setStockForm(buildInitialStockForm());
        message.success('Stok kaydedildi.');
      } else {
        message.error(response?.resultMessage || 'Stok kaydedilemedi.');
      }
    } catch (error) {
      console.error('Stok kaydedilemedi:', error);
      message.error('Stok kaydedilemedi.');
    } finally {
      setSavingStock(false);
    }
  };

  const handleDeleteStock = async (guid) => {
    AntdModal.confirm({
      title: 'Stok Silme',
      content: 'Bu toner stok kaydını silmek istediğinize emin misiniz?',
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await deleteTonerStock(guid);
          if (response?.isSuccess) {
            await loadTonerStocks();
            await loadCatalogs();
            await loadDashboard();
            message.success('Stok kaydı silindi.');
          } else {
            message.error(response?.resultMessage || 'Stok silinemedi.');
          }
        } catch (error) {
          console.error('Stok silinemedi:', error);
          message.error('Stok silinemedi.');
        }
      }
    });
  };

  const openEventModal = (type, printerGuid) => {
    const targetPrinterId = printerGuid || selectedPrinterId;
    if (!targetPrinterId) {
      message.warning('Önce yazıcı seçin.');
      return;
    }

    setSelectedPrinterId(targetPrinterId);
    const targetPrinter =
      printers.find((printer) => printer.guid === targetPrinterId) || selectedPrinter;
    const printerToners = collectToners(targetPrinter, catalogs);
    setEventForm({
      ...initialEventForm(targetPrinterId),
      eventType: type,
      printerCatalogTonerGuid:
        type === 'toner_change' && printerToners.length ? printerToners[0].guid : ''
    });
    setShowEventModal(true);
  };

  const handleEventInput = (event) => {
    const { name, value } = event.target;
    setEventForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEvent = async (event) => {
    event.preventDefault();
    if (!eventForm.printerGuid) {
      message.warning('Yazıcı seçmelisiniz.');
      return;
    }

    if (eventForm.eventType === 'toner_change' && availableModalToners.length && !eventForm.printerCatalogTonerGuid) {
      message.warning('Değişen toneri seçmelisiniz.');
      return;
    }

    setSavingEvent(true);
    try {
      const buildDescription = () => {
        const note = eventForm.description?.trim();
        const selectedToner = availableModalToners.find((toner) => toner.guid === eventForm.printerCatalogTonerGuid);
        if (eventForm.eventType === 'toner_change' && selectedToner) {
          return note ? `Toner: ${selectedToner.name} - ${note}` : `Toner: ${selectedToner.name}`;
        }
        return note || undefined;
      };

      const payload = {
        printerGuid: eventForm.printerGuid,
        assignmentGuid: null,
        printerCatalogTonerGuid: eventForm.printerCatalogTonerGuid || null,
        eventType: eventForm.eventType,
        description: buildDescription(),
        eventDate: eventForm.eventDate ? dayjs(eventForm.eventDate).toISOString() : undefined
      };

      const response = await createPrinterEvent(payload);
      if (response?.isSuccess) {
        setShowEventModal(false);
        await Promise.all([loadEvents(eventForm.printerGuid), loadDashboard(), loadTonerStocks()]);
        message.success('Kayıt oluşturuldu.');
      } else {
        message.error(response?.resultMessage || 'Kayıt oluşturulamadı.');
      }
    } catch (error) {
      console.error('Kayıt oluşturulamadı:', error);
      message.error('Kayıt oluşturulamadı.');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeletePrinter = async (printer) => {
    AntdModal.confirm({
      title: 'Yazıcı Silme',
      content: `${printer.name} yazıcısını silmek istediğinize emin misiniz?`,
      okText: 'Evet',
      okType: 'danger',
      cancelText: 'Hayır',
      onOk: async () => {
        try {
          const response = await deletePrinter(printer.guid);
          if (response?.isSuccess) {
            if (printer.guid === selectedPrinterId) {
              setSelectedPrinterId('');
            }
            await loadDashboard();
            message.success('Yazıcı silindi.');
          } else {
            message.error(response?.resultMessage || 'Yazıcı silinemedi.');
          }
        } catch (error) {
          console.error('Yazıcı silinemedi:', error);
          message.error('Yazıcı silinemedi.');
        }
      }
    });
  };

  const isPanelActive = activePanel === 'catalog' || activePanel === 'stock';

  return (
    <div className="container-fluid">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
        <div>
          <h2 className="mb-1">Toner Yönetimi</h2>
          <p className="text-muted mb-0">Birim bazlı Yazıcı Kayıtları, toner değişimleri ve arıza raporları</p>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {isPanelActive && (
            <Button
              variant="outline-secondary"
              onClick={() => {
                setActivePanel('');
                setCatalogForm(buildInitialCatalogForm());
                setStockForm(buildInitialStockForm());
              }}
              aria-label="Ana görünüme dön"
              title="Ana Görünüm"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M3 9.5 12 3l9 6.5" />
                <path d="M5 10v10h5.5v-5h3v5H19V10" />
              </svg>
            </Button>
          )}
          <Button onClick={handleOpenPrinterModal}>Yazıcı Ekle</Button>
          <Button
            variant={activePanel === 'catalog' ? 'primary' : 'outline-secondary'}
            onClick={() => {
              if (activePanel === 'catalog') {
                setActivePanel('');
              } else {
                setCatalogForm(buildInitialCatalogForm());
                setActivePanel('catalog');
              }
            }}
          >
            Yazıcıları Yönet
          </Button>
          <Button
            variant={activePanel === 'stock' ? 'primary' : 'outline-secondary'}
            onClick={() => {
              if (activePanel === 'stock') {
                setActivePanel('');
              } else {
                setStockForm(buildInitialStockForm());
                setActivePanel('stock');
              }
            }}
          >
            Stok
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <>
          {!isPanelActive && (
            <Card className="mb-4">
              <Card.Body>
                <Row className="g-3">
                  <Col md={6} lg={4}>
                    <Form.Group>
                      <Form.Label>Birim</Form.Label>
                      <Form.Select value={selectedDepartment} onChange={(e) => setSelectedDepartment(e.target.value)}>
                        {departmentOptions.map((department) => (
                          <option key={department.value} value={department.value}>
                            {department.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {activePanel === 'catalog' && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title as="h5" className="mb-0">
                  Yazıcıları Yönet
                </Card.Title>
                <Button variant="link" size="sm" onClick={() => setActivePanel('')}>
                  Kapat
                </Button>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleCatalogSubmit}>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>Marka</Form.Label>
                      <Form.Control name="brand" value={catalogForm.brand} onChange={handleCatalogInput} required />
                    </Col>
                    <Col md={6}>
                      <Form.Label>Model</Form.Label>
                      <Form.Control name="model" value={catalogForm.model} onChange={handleCatalogInput} required />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Kullanılan Tonerler</Form.Label>
                      {catalogForm.tonerStockGuids.map((tonerGuid, index) => (
                        <div className="d-flex gap-2 mb-2" key={`toner-${index}`}>
                          <Form.Select
                            value={tonerGuid}
                            onChange={(event) => handleCatalogTonerSelect(index, event.target.value)}
                            disabled={!tonerStocks.length}
                          >
                            <option value="">Toner Seçin</option>
                            {tonerStocks.map((stock) => (
                              <option key={stock.guid} value={stock.guid}>
                                {stock.name} (Stok: {stock.quantity})
                              </option>
                            ))}
                          </Form.Select>
                          {catalogForm.tonerStockGuids.length > 1 && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              type="button"
                              onClick={() => removeCatalogTonerRow(index)}
                            >
                              Sil
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="outline-primary" type="button" onClick={addCatalogTonerRow}>
                        Toner Satırı Ekle
                      </Button>
                      <Form.Text muted>Listede görünmesi için stoklarda toner tanımlamanız gerekir.</Form.Text>
                    </Col>
                    <Col md={12}>
                      <Form.Label>Not</Form.Label>
                      <Form.Control name="notes" value={catalogForm.notes} onChange={handleCatalogInput} />
                    </Col>
                  </Row>
                  <div className="d-flex gap-2 mb-3">
                    <Button type="submit" disabled={savingCatalog}>
                      {savingCatalog ? 'Kaydediliyor...' : catalogForm.guid ? 'Güncelle' : 'Ekle'}
                    </Button>
                    {catalogForm.guid && (
                      <Button type="button" variant="secondary" onClick={() => setCatalogForm(buildInitialCatalogForm())}>
                        Yeni Kayıt
                      </Button>
                    )}
                  </div>
                </Form>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Marka</th>
                        <th>Model</th>
                        <th>Tonerler</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {catalogs.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted">
                            Henüz kayıt yok.
                          </td>
                        </tr>
                      )}
                      {catalogs.map((catalog) => (
                        <tr key={catalog.guid} className="align-middle">
                          <td>{catalog.brand}</td>
                          <td>{catalog.model}</td>
                          <td>
                            {catalog.toners?.length ? (
                              <div className="d-flex flex-wrap gap-1">
                                {catalog.toners.map((toner) => (
                                  <Badge bg="secondary" key={`${catalog.guid}-${toner.guid}`}>
                                    {toner.tonerStockName}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2">
                              <Button size="sm" variant="outline-primary" onClick={() => handleEditCatalog(catalog)}>
                                Düzenle
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteCatalog(catalog.guid)}>
                                Sil
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {activePanel === 'stock' && (
            <Card className="mb-4">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title as="h5">
                  Toner Stokları
                </Card.Title>
                <Button variant="link" size="sm" onClick={() => setActivePanel('')}>
                  Kapat
                </Button>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleSaveStock}>
                  <Row className="g-3 mb-3">
                    <Col md={6}>
                      <Form.Label>Toner Adı</Form.Label>
                      <Form.Control name="name" value={stockForm.name} onChange={handleStockInput} required />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Adet</Form.Label>
                      <Form.Control
                        name="quantity"
                        type="number"
                        value={stockForm.quantity}
                        onChange={handleStockInput}
                        min={0}
                        required
                      />
                    </Col>
                    <Col md={12}>
                      <Form.Label>Not</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        name="notes"
                        value={stockForm.notes}
                        onChange={handleStockInput}
                      />
                    </Col>
                  </Row>
                  <div className="d-flex gap-2 mb-3">
                    <Button type="submit" disabled={savingStock}>
                      {savingStock ? 'Kaydediliyor...' : stockForm.guid ? 'Güncelle' : 'Ekle'}
                    </Button>
                    {stockForm.guid && (
                      <Button type="button" variant="secondary" onClick={() => setStockForm(buildInitialStockForm())}>
                        Yeni Kayıt
                      </Button>
                    )}
                  </div>
                </Form>
                <div className="table-responsive">
                  <Table hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Toner</th>
                        <th>Adet</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {tonerStocks.length === 0 && (
                        <tr>
                          <td colSpan={3} className="text-center text-muted">
                            Henüz stok kaydı yok.
                          </td>
                        </tr>
                      )}
                      {tonerStocks.map((stock) => (
                        <tr key={stock.guid} className="align-middle">
                          <td>{stock.name}</td>
                          <td>{stock.quantity}</td>
                          <td className="text-end">
                            <div className="d-inline-flex gap-2">
                              <Button size="sm" variant="outline-primary" onClick={() => handleOpenStockEditor(stock)}>
                                Düzenle
                              </Button>
                              <Button size="sm" variant="outline-danger" onClick={() => handleDeleteStock(stock.guid)}>
                                Sil
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          )}

          {!isPanelActive && (
            <Row className="g-4">
              <Col lg={6}>
                <Card>
                  <Card.Header>
                    <Card.Title as="h5">Bu Birimdeki Yazıcılar</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {departmentPrinters.length === 0 ? (
                      <p className="text-muted px-3 py-4 mb-0">Bu birimde kayıtlı yazıcı yok.</p>
                    ) : (
                      <Table hover responsive className="mb-0">
                        <thead>
                          <tr>
                            <th>Yazıcı</th>
                            <th>Kayıtlar</th>
                            <th className="text-end">İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {departmentPrinters.map((printer) => {
                            const printerToners = collectToners(printer, catalogs);
                            const isActive = printer.guid === selectedPrinterId;
                            const users =
                              printer.assignments
                                ?.map((assignment) => assignment.personName?.trim())
                                .filter((name) => !!name) || [];
                            const departmentAssignment = printer.assignments?.find(
                              (assignment) => assignment.departmentName === selectedDepartment && assignment.isActive
                            );
                            return (
                              <tr
                                key={printer.guid}
                                className={isActive ? 'table-active' : ''}
                                onClick={() => setSelectedPrinterId(printer.guid)}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  <div className="fw-semibold">{printer.name}</div>
                                  <div className="text-muted small">
                                    {printer.catalogBrand && printer.catalogModel
                                      ? `${printer.catalogBrand} ${printer.catalogModel}`
                                      : printer.model || '-'}
                                  </div>
                                  <div className="text-muted small mt-1">
                                    Tonerler:{' '}
                                    {printerToners.length ? (
                                      <span className="d-inline-flex flex-wrap gap-1 align-items-center">
                                        {printerToners.map((toner) => (
                                          <Badge bg="info" key={`${printer.guid}-${toner.guid}`}>
                                            {toner.name || '-'}
                                          </Badge>
                                        ))}
                                      </span>
                                    ) : (
                                      '-'
                                    )}
                                  </div>
                                  {users.length > 0 && (
                                    <div className="text-muted small">
                                      Kullanan: {users.join(', ')}
                                    </div>
                                  )}
                                  {departmentAssignment && (departmentAssignment.roomName || departmentAssignment.roomNumber) && (
                                    <div className="text-muted small">
                                      Oda:{' '}
                                      {departmentAssignment.roomName
                                        ? departmentAssignment.roomName
                                        : '-'}
                                      {departmentAssignment.roomNumber
                                        ? ` (${departmentAssignment.roomNumber})`
                                        : ''}
                                    </div>
                                  )}
                                  <div className="text-muted small">Kullanım sayısı: {printer.assignments?.length || 0}</div>
                                </td>
                                <td>
                                  <Badge bg="success" className="me-2">
                                    Toner {printer.tonerChangeCount || 0}
                                  </Badge>
                                  <Badge bg="danger">Arıza {printer.faultCount || 0}</Badge>
                                </td>
                                <td className="text-end">
                                  <div className="d-flex justify-content-end flex-wrap gap-2">
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleEditPrinter(printer, departmentAssignment);
                                      }}
                                    >
                                      Düzenle
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleDeletePrinter(printer);
                                      }}
                                    >
                                      Sil
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openEventModal('toner_change', printer.guid);
                                      }}
                                    >
                                      Toner Değişimi
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        openEventModal('fault', printer.guid);
                                      }}
                                    >
                                      Arıza Kaydı
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={6}>
                <Card>
                  <Card.Header>
                    <Card.Title as="h5">Toner / Arıza Kayıtları</Card.Title>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {selectedPrinterId ? (
                      events.length === 0 ? (
                        <p className="text-muted px-3 py-4 mb-0">Bu yazıcı için kayıt bulunmuyor.</p>
                      ) : (
                        <Table responsive hover className="mb-0">
                          <thead>
                            <tr>
                              <th>Tarih</th>
                              <th>Tür</th>
                              <th>Kullanım</th>
                              <th>Açıklama</th>
                              <th>Kayıt Eden</th>
                            </tr>
                          </thead>
                          <tbody>
                            {events.map((event) => (
                              <tr key={event.guid}>
                                <td>{dayjs(event.eventDate).format('DD.MM.YYYY HH:mm')}</td>
                                <td>
                                  <Badge
                                    bg={
                                      event.eventType === 'toner_change'
                                        ? 'success'
                                        : event.eventType === 'fault'
                                          ? 'danger'
                                          : 'info'
                                    }
                                  >
                                    {EVENT_TYPE_LABELS[event.eventType] || event.eventType}
                                  </Badge>
                                </td>
                                <td>{event.assignmentSummary || 'Genel Kullanım'}</td>
                                <td>
                                  {event.tonerStockName && event.eventType === 'toner_change' ? (
                                    <div className="fw-semibold">{event.tonerStockName}</div>
                                  ) : null}
                                  <div>{event.description || (!event.tonerStockName ? '-' : '')}</div>
                                </td>
                                <td>{event.recordedByName || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )
                    ) : (
                      <p className="text-muted px-3 py-4 mb-0">Önce bir yazıcı seçin.</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}

      <Modal show={showPrinterModal} onHide={() => setShowPrinterModal(false)} size="lg" centered>
        <Form onSubmit={handleSavePrinter}>
          <Modal.Header closeButton>
            <Modal.Title>{editingPrinter ? 'Yazıcıyı Düzenle' : 'Yazıcı Ekle'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Birim</Form.Label>
                <Form.Select
                  name="departmentName"
                  value={printerForm.departmentName}
                  onChange={handlePrinterInput}
                  required
                >
                  <option value="">Birim seçin</option>
                  {departmentOptions.map((department) => (
                    <option key={department.value} value={department.value}>
                      {department.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label>Yazıcı Adı</Form.Label>
                <Form.Control name="name" value={printerForm.name} onChange={handlePrinterInput} required />
              </Col>
              <Col md={6}>
                <Form.Label>Marka / Model</Form.Label>
                <Form.Select
                  name="printerCatalogGuid"
                  value={printerForm.printerCatalogGuid}
                  onChange={handlePrinterInput}
                >
                  <option value="">Seçiniz</option>
                  {catalogs.map((catalog) => (
                    <option key={catalog.guid} value={catalog.guid}>
                      {catalog.brand} - {catalog.model}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text muted>
                  Yeni marka ve modeller için Yazıcıları Yönet sekmesini kullanabilirsiniz.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Oda Adı</Form.Label>
                <Form.Control name="roomName" value={printerForm.roomName} onChange={handlePrinterInput} />
              </Col>
              <Col md={6}>
                <Form.Label>Oda No</Form.Label>
                <Form.Control name="roomNumber" value={printerForm.roomNumber} onChange={handlePrinterInput} />
              </Col>
              <Col md={6}>
                <Form.Label>Kişi (opsiyonel)</Form.Label>
                <Form.Control name="personName" value={printerForm.personName} onChange={handlePrinterInput} />
              </Col>
              <Col md={12}>
                <Form.Label>Not</Form.Label>
                <Form.Control as="textarea" rows={2} name="notes" value={printerForm.notes} onChange={handlePrinterInput} />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPrinterModal(false)}>
              Kapat
            </Button>
            <Button type="submit" disabled={savingPrinter}>
              {savingPrinter ? 'Kaydediliyor...' : editingPrinter ? 'Güncelle' : 'Kaydet'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showEventModal} onHide={() => setShowEventModal(false)} centered>
        <Form onSubmit={handleSaveEvent}>
          <Modal.Header closeButton>
            <Modal.Title>{EVENT_TYPE_LABELS[eventForm.eventType]}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <input type="hidden" name="printerGuid" value={eventForm.printerGuid} />
            <Form.Group className="mb-3">
              <Form.Label>Yazıcı</Form.Label>
              <Form.Control plaintext readOnly value={modalPrinter?.name || 'Seçilmedi'} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Birim</Form.Label>
              <Form.Control plaintext readOnly value={selectedDepartment ? getDepartmentLabel(selectedDepartment) : '-'} />
            </Form.Group>
            {eventForm.eventType === 'toner_change' && (
              <Form.Group className="mb-3">
                <Form.Label>Değişen Toner</Form.Label>
                <Form.Select
                  name="printerCatalogTonerGuid"
                  value={eventForm.printerCatalogTonerGuid}
                  onChange={handleEventInput}
                  disabled={!availableModalToners.length}
                  required={availableModalToners.length > 0}
                >
                  <option value="">
                    {availableModalToners.length ? 'Toner Seçin' : 'Tanımlı toner yok'}
                  </option>
                  {availableModalToners.map((toner) => (
                    <option key={toner.guid} value={toner.guid}>
                      {toner.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Açıklama</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={eventForm.description} onChange={handleEventInput} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Tarih</Form.Label>
              <Form.Control type="datetime-local" name="eventDate" value={eventForm.eventDate} onChange={handleEventInput} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEventModal(false)}>
              Kapat
            </Button>
            <Button type="submit" disabled={savingEvent}>
              {savingEvent ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default PrinterManagement;
